import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { UserPlus, Loader2, Shield, Users, Mail, Phone, ShieldCheck, KeyRound, LogOut, ShieldOff } from "lucide-react";
import { sec } from "@/lib/securityApi";
import { getPortalUsers, createUser } from "@/lib/portalApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const ROLE_STYLES = {
  admin: "bg-venka-blue/20 text-venka-green-light border-venka-blue/40",
  agent: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  customer: "bg-slate-700/40 text-slate-300 border-slate-600",
};

const selectClasses =
  "h-10 w-full rounded-md border border-white/15 bg-slate-950/60 px-3 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-venka-blue/40";

const emptyForm = { name: "", email: "", password: "", phone: "", role: "agent", preferred_language: "en", team: "", office: "" };

export const PortalUsers = () => {
  const { t, i18n } = useTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [secInfo, setSecInfo] = useState({});
  const lg = (i18n.language || "en").slice(0, 2);
  const TX = {
    en: { security: "Security", last: "Last sign-in", never: "Never", reset: "Send password reset link", out: "Sign out everywhere", off: "Turn off 2FA (lost phone)", sent: "Done", confirmOff: "Turn off two-factor authentication for this user? Do it only after checking their identity." },
    pt: { security: "Segurança", last: "Último acesso", never: "Nunca", reset: "Enviar link de redefinição", out: "Terminar todas as sessões", off: "Desativar 2FA (telemóvel perdido)", sent: "Feito", confirmOff: "Desativar a autenticação de dois fatores deste utilizador? Faça-o só depois de confirmar a identidade." },
    fr: { security: "Sécurité", last: "Dernière connexion", never: "Jamais", reset: "Envoyer un lien de réinitialisation", out: "Déconnecter partout", off: "Désactiver la 2FA (téléphone perdu)", sent: "Fait", confirmOff: "Désactiver l'authentification à deux facteurs de cet utilisateur ? Uniquement après avoir vérifié son identité." },
  }[lg] || {};
  const secAction = async (u, action) => {
    if (action === "disable_2fa" && !window.confirm(TX.confirmOff)) return;
    try { await sec.adminAction(u.id, action); toast.success(TX.sent); setSecInfo(await sec.adminOverview()); } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const load = useCallback(async () => {
    try {
      sec.adminOverview().then(setSecInfo).catch(() => {});
      const data = await getPortalUsers();
      setUsers(Array.isArray(data) ? data : []);
    } catch (e) {
      /* handled */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setError("");
    if (!form.name.trim() || !form.email.trim() || form.password.length < 8 || !/[A-Za-z]/.test(form.password) || !/\d/.test(form.password)) {
      setError(t("portal.users.validation8", "Name, email and a password of at least 8 characters with letters and a number are required."));
      return;
    }
    setSaving(true);
    try {
      await createUser({ ...form, name: form.name.trim(), email: form.email.trim() });
      toast.success(t("portal.users.created", "User created"));
      setOpen(false);
      setForm(emptyForm);
      setLoading(true);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || t("portal.users.error", "Could not create the user."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="portal-users-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6 text-venka-green-light" /> {t("portal.users.title", "Team & Customers")}
          </h1>
          <p className="text-sm text-slate-400">{t("portal.users.subtitle", "Create staff members and customer accounts.")}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full brand-gradient text-white hover:opacity-90" data-testid="users-new-button">
              <UserPlus className="h-4 w-4 mr-1.5" /> {t("portal.users.new", "New user")}
            </Button>
          </DialogTrigger>
          <DialogContent className="glass-panel border-white/15 text-slate-100">
            <DialogHeader>
              <DialogTitle>{t("portal.users.new", "New user")}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="mb-1 block">{t("portal.users.role", "Role")}</Label>
                <select className={selectClasses} value={form.role} onChange={set("role")} data-testid="users-role-select">
                  <option value="agent">Agent (staff)</option>
                  <option value="admin">Administrator</option>
                  <option value="customer">Customer</option>
                </select>
              </div>
              {form.role !== "customer" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="mb-1 block">{t("portal.users.team", "Team")}</Label>
                    <select className={selectClasses} value={form.team} onChange={set("team")} data-testid="users-team-select">
                      <option value="">General</option>
                      <option value="immigration">Immigration</option>
                    </select>
                  </div>
                  <div>
                    <Label className="mb-1 block">{t("portal.users.office", "Office")}</Label>
                    <select className={selectClasses} value={form.office} onChange={set("office")} data-testid="users-office-select">
                      <option value="">—</option>
                      <option value="luanda">Luanda</option>
                      <option value="tallinn">Tallinn</option>
                      <option value="lisbon">Lisbon</option>
                    </select>
                  </div>
                </div>
              )}
              <div>
                <Label className="mb-1 block">{t("portal.users.name", "Full name")}</Label>
                <Input value={form.name} onChange={set("name")} className="bg-slate-950/60 border-white/15" data-testid="users-name-input" />
              </div>
              <div>
                <Label className="mb-1 block">{t("portal.users.email", "Email")}</Label>
                <Input type="email" value={form.email} onChange={set("email")} className="bg-slate-950/60 border-white/15" data-testid="users-email-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1 block">{t("portal.users.password", "Password")}</Label>
                  <Input type="text" value={form.password} onChange={set("password")} className="bg-slate-950/60 border-white/15" data-testid="users-password-input" />
                </div>
                <div>
                  <Label className="mb-1 block">{t("portal.users.phone", "Phone")}</Label>
                  <Input value={form.phone} onChange={set("phone")} className="bg-slate-950/60 border-white/15" data-testid="users-phone-input" />
                </div>
              </div>
              <div>
                <Label className="mb-1 block">{t("portal.users.language", "Preferred language")}</Label>
                <select className={selectClasses} value={form.preferred_language} onChange={set("preferred_language")} data-testid="users-language-select">
                  <option value="en">English</option>
                  <option value="pt">Português</option>
                  <option value="fr">Français</option>
                </select>
              </div>
              {error && <p className="text-sm text-red-400" data-testid="users-error">{error}</p>}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)} className="text-slate-300 hover:text-white">
                {t("portal.modal.cancel", "Cancel")}
              </Button>
              <Button onClick={submit} disabled={saving} className="brand-gradient text-white" data-testid="users-save-button">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("portal.users.save", "Create user")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <section className="rounded-2xl border border-white/10 bg-card/60 overflow-hidden" data-testid="users-table">
        {loading ? (
          <div className="grid place-items-center py-16 text-slate-500"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-slate-400 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3">{t("portal.users.name", "Name")}</th>
                <th className="text-left px-4 py-3 hidden sm:table-cell">{t("portal.users.email", "Email")}</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">{t("portal.users.phone", "Phone")}</th>
                <th className="text-left px-4 py-3">{t("portal.users.role", "Role")}</th>
                <th className="text-left px-4 py-3">{TX.security}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-t border-white/5" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 font-medium text-slate-100">{u.name}</td>
                  <td className="px-4 py-3 text-slate-300 hidden sm:table-cell"><span className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5 text-slate-500" />{u.email}</span></td>
                  <td className="px-4 py-3 text-slate-300 hidden md:table-cell">{u.phone ? <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-slate-500" />{u.phone}</span> : "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${ROLE_STYLES[u.role] || ROLE_STYLES.customer}`}>
                      {u.role === "admin" && <Shield className="h-3 w-3" />}{u.role}
                    </span>
                    {u.team === "immigration" && (
                      <span className="ml-1.5 inline-flex items-center rounded-full border border-venka-blue/40 bg-venka-blue/15 px-2 py-0.5 text-[10px] font-semibold text-venka-green-light">
                        Immigration{u.office ? ` · ${u.office}` : ""}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${secInfo[u.id]?.two_factor_enabled ? "bg-venka-green/15 text-venka-green" : "bg-slate-700/40 text-slate-400"}`} data-testid={`user-2fa-${u.id}`}>
                        <ShieldCheck className="h-3 w-3" />2FA {secInfo[u.id]?.two_factor_enabled ? "✓" : "–"}
                      </span>
                      <span className="text-[11px] text-slate-500" title={TX.last}>{secInfo[u.id]?.last_login_at ? new Date(secInfo[u.id].last_login_at).toLocaleDateString() : TX.never}</span>
                      <button type="button" title={TX.reset} onClick={() => secAction(u, "send_reset")} className="rounded p-1 text-slate-400 hover:bg-white/5 hover:text-slate-100" data-testid={`user-reset-${u.id}`}><KeyRound className="h-3.5 w-3.5" /></button>
                      <button type="button" title={TX.out} onClick={() => secAction(u, "sign_out")} className="rounded p-1 text-slate-400 hover:bg-white/5 hover:text-slate-100"><LogOut className="h-3.5 w-3.5" /></button>
                      {secInfo[u.id]?.two_factor_enabled && <button type="button" title={TX.off} onClick={() => secAction(u, "disable_2fa")} className="rounded p-1 text-red-400 hover:bg-red-500/10"><ShieldOff className="h-3.5 w-3.5" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-slate-500">{t("portal.users.empty", "No users yet.")}</td></tr>
              )}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
};

export default PortalUsers;
